import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useModalBackButton from '../../hooks/useModalBackButton';
import useToast from '../../hooks/useToast';
import {
  Ban,
  BriefcaseBusiness,
  Edit2,
  Eye,
  FileSpreadsheet,
  FileText,
  History,
  Hash,
  Loader2,
  MapPin,
  Phone,
  Save,
  Search,
  ShoppingBag,
  Star,
  Download,
  User,
  Users,
  Wallet,
  X,
  XCircle
} from 'lucide-react';
import { API_URL } from '../../config';
import { CompactListSkeleton } from '../../components/Skeleton';
import ModalShell from '../../components/ModalShell';
import FloatingField from '../../components/FloatingField';
import { safeFetch } from '../../utils/safeFetch';
import { haptic } from '../../utils/vibration';

const CLIENT_TYPE_OPTIONS = [
  { value: 'individual', label: 'Физ. лица', icon: User },
  { value: 'company', label: 'Юр. лица', icon: BriefcaseBusiness }
];

const CLIENT_SORT_OPTIONS = [
  { field: 'last_order_date', direction: 'desc', label: 'Сначала клиенты с последним заказом', icon: History },
  { field: 'total_spent', direction: 'desc', label: 'Сначала клиенты с большей суммой заказов', icon: Wallet },
  { field: 'total_orders', direction: 'desc', label: 'Сначала клиенты с большим количеством заказов', icon: ShoppingBag },
  { field: 'registered_date', direction: 'desc', label: 'Сначала новые клиенты', icon: User },
  { field: 'bonus_balance', direction: 'desc', label: 'Сначала клиенты с большими бонусами', icon: Star },
  { field: 'name', direction: 'asc', label: 'По имени или названию компании', icon: Users },
  { field: 'id', direction: 'desc', label: 'Сначала последние добавленные клиенты', icon: Hash },
];

const EXPORT_FORMAT_OPTIONS = [
  { value: 'xlsx', label: 'Excel', icon: FileSpreadsheet },
  { value: 'docx', label: 'Word', icon: FileText }
];

const CLIENT_NOTE_MAX_LENGTH = 400;
const REFERRAL_SOURCE_LABELS = {
  google: 'Google',
  yandex: 'Яндекс',
  instagram: 'Instagram',
  vk: 'ВКонтакте',
  telegram: 'Telegram',
  friend: 'Рекомендация',
  advertising: 'Реклама',
  other: 'Другое'
};

const CLIENT_EXPORT_FIELD_LABELS = {
  individual: {
    id: 'ID',
    last_name: 'Фамилия',
    first_name: 'Имя',
    middle_name: 'Отчество',
    phone: 'Телефон',
    active: 'Активен',
    vip: 'VIP',
    undesirable: 'Нежелательный',
    preferred_pickup_time: 'Предпочтительное время забора',
    preferred_delivery_time: 'Предпочтительное время доставки',
    preferred_payment_method: 'Предпочтительный способ оплаты',
    referral_source: 'Источник',
    referral_code: 'Реферальный код',
    notes: 'Заметка',
    all_addresses: 'Адреса',
    all_contacts: 'Контакты',
    total_orders: 'Всего заказов',
    completed_orders: 'Завершено',
    active_orders: 'Активных заказов',
    cancelled_orders: 'Отменено',
    total_spent: 'Сумма заказов',
    bonus_balance: 'Бонусы',
    first_order_date: 'Первый заказ',
    last_order_date: 'Последний заказ',
    registered_date: 'Дата регистрации'
  },
  company: {
    id: 'ID',
    company_name: 'Название компании',
    contact_person: 'Контактное лицо',
    phone: 'Телефон',
    active: 'Активен',
    vip: 'VIP',
    undesirable: 'Нежелательный',
    inn: 'ИНН',
    kpp: 'КПП',
    legal_type: 'Тип юрлица',
    full_name: 'Полное наименование',
    short_name: 'Краткое наименование',
    ogrn: 'ОГРН',
    position: 'Должность',
    bank_name: 'Банк',
    bank_account: 'Расчетный счет',
    bik: 'БИК',
    correspondent_account: 'Корр. счет',
    legal_address: 'Юридический адрес',
    preferred_pickup_time: 'Предпочтительное время забора',
    preferred_delivery_time: 'Предпочтительное время доставки',
    preferred_payment_method: 'Предпочтительный способ оплаты',
    referral_source: 'Источник',
    referral_code: 'Реферальный код',
    notes: 'Заметка',
    all_addresses: 'Адреса',
    all_contacts: 'Контакты',
    total_orders: 'Всего заказов',
    completed_orders: 'Завершено',
    active_orders: 'Активных заказов',
    cancelled_orders: 'Отменено',
    total_spent: 'Сумма заказов',
    bonus_balance: 'Бонусы',
    first_order_date: 'Первый заказ',
    last_order_date: 'Последний заказ',
    registered_date: 'Дата регистрации'
  }
};

const EXPORT_FIELD_GROUPS = {
  individual: [
    { title: 'Основное', fields: ['id', 'last_name', 'first_name', 'middle_name', 'phone', 'active', 'vip', 'undesirable'] },
    { title: 'Предпочтения', fields: ['preferred_pickup_time', 'preferred_delivery_time', 'preferred_payment_method', 'referral_source', 'referral_code', 'notes'] },
    { title: 'Контакты и адреса', fields: ['all_addresses', 'all_contacts'] },
    { title: 'Статистика', fields: ['total_orders', 'completed_orders', 'active_orders', 'cancelled_orders', 'total_spent', 'bonus_balance', 'first_order_date', 'last_order_date', 'registered_date'] }
  ],
  company: [
    { title: 'Основное', fields: ['id', 'company_name', 'contact_person', 'phone', 'active', 'vip', 'undesirable'] },
    { title: 'Реквизиты', fields: ['inn', 'kpp', 'legal_type', 'full_name', 'short_name', 'ogrn', 'position', 'bank_name', 'bank_account', 'bik', 'correspondent_account', 'legal_address'] },
    { title: 'Предпочтения', fields: ['preferred_pickup_time', 'preferred_delivery_time', 'preferred_payment_method', 'referral_source', 'referral_code', 'notes'] },
    { title: 'Контакты и адреса', fields: ['all_addresses', 'all_contacts'] },
    { title: 'Статистика', fields: ['total_orders', 'completed_orders', 'active_orders', 'cancelled_orders', 'total_spent', 'bonus_balance', 'first_order_date', 'last_order_date', 'registered_date'] }
  ]
};

const EXPORT_DEFAULT_FIELDS = {
  individual: ['last_name', 'first_name', 'middle_name', 'phone', 'active', 'vip', 'undesirable', 'total_orders', 'total_spent', 'bonus_balance', 'last_order_date'],
  company: ['company_name', 'contact_person', 'phone', 'inn', 'kpp', 'active', 'vip', 'undesirable', 'total_orders', 'total_spent', 'last_order_date']
};

const ORDER_STATUS_LABELS = {
  new: 'Новый',
  pickup: 'Забор',
  washing: 'Стирка',
  cleaning: 'Чистка',
  drying_packing: 'Сушка и упаковка',
  approval: 'Согласование',
  delivery: 'Доставка',
  handover: 'Выдача',
  completed: 'Завершен',
  cancelled: 'Отменен'
};

const SERVICE_TYPE_LABELS = {
  washing: 'Стирка',
  cleaning: 'Химчистка',
  extra_washing: 'Доп. стирка',
  extra_cleaning: 'Доп. чистка',
  extra_order: 'Доп. услуги'
};

const PAYMENT_STATUS_LABELS = {
  pending: 'Не оплачен',
  paid: 'Оплачен',
  refunded: 'Возврат'
};

const formatMoney = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return '—';
  }
};

const getPhoneLocalDigits = (value) => {
  const source = String(value || '').trim();
  let digits = String(value || '').replace(/\D/g, '');

  if (source.startsWith('+7')) {
    digits = digits.slice(1);
  } else if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
    digits = digits.slice(1);
  }

  return digits.slice(0, 10);
};

const maskPhoneInput = (value) => {
  const digits = getPhoneLocalDigits(value);
  if (!digits) return '';

  let masked = '+7';
  if (digits.length > 0) {
    masked += `(${digits.slice(0, Math.min(3, digits.length))}`;
  }
  if (digits.length >= 3) {
    masked += ')';
  }
  if (digits.length > 3) {
    masked += `-${digits.slice(3, Math.min(6, digits.length))}`;
  }
  if (digits.length > 6) {
    masked += `-${digits.slice(6, Math.min(8, digits.length))}`;
  }
  if (digits.length > 8) {
    masked += `-${digits.slice(8, 10)}`;
  }
  return masked;
};

const normalizePhoneForSubmit = (value) => {
  const digits = getPhoneLocalDigits(value);
  if (digits.length !== 10) return '';
  return `+7${digits}`;
};

const formatPhoneNumber = (value) => {
  const source = String(value || '').trim();
  if (!source) return 'Телефон не указан';

  const normalized = normalizePhoneForSubmit(source);
  return normalized ? maskPhoneInput(normalized) : source;
};

const getReferralSourceLabel = (value) => REFERRAL_SOURCE_LABELS[String(value || '').trim()] || value || '—';

const getClientName = (client) => {
  if (!client) return 'Без названия';
  if (client.type === 'company' || client.company_name) {
    return client.company_name || client.full_name || 'Без названия';
  }
  return [client.last_name, client.first_name, client.middle_name].filter(Boolean).join(' ').trim() || 'Без имени';
};

const getClientSubtitle = (client) => {
  if (!client) return '';
  if (client.type === 'company' || client.company_name) {
    return client.contact_person || client.full_name || client.short_name || '';
  }
  return client.phone ? formatPhoneNumber(client.phone) : '';
};

const getExportFieldOrder = (clientKind) => (
  (EXPORT_FIELD_GROUPS[clientKind] || []).flatMap((group) => group.fields)
);

const buildClientExportFileName = ({ clientKind, format }) => {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);
  return `clients_${clientKind}_${stamp}.${format}`;
};

function ManagementSegmentedControl({ value, options, onChange, compact = false }) {
  return (
    <div className="grid gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map((option) => {
        const Icon = option.icon;
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              if (option.value !== value) haptic('selection');
              onChange(option.value);
            }}
            className={`flex items-center justify-center rounded-lg font-medium transition-all ${
              compact ? 'px-3 py-2 text-[13px]' : 'px-4 py-2.5 text-[13px]'
            } ${
              active
                ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100'
            }`}
          >
            {Icon ? <Icon className={compact ? 'mr-1.5 h-4 w-4' : 'mr-2 h-4 w-4'} /> : null}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ClientsListSkeleton() {
  return (
    <div className="space-y-2 animate-pulse animate-slide-up">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={`client-skeleton-${index}`} className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 shrink-0 rounded-xl bg-gray-200 dark:bg-gray-700" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 w-44 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-32 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex flex-wrap gap-2">
                <div className="h-3 w-20 rounded-full bg-gray-100 dark:bg-gray-800" />
                <div className="h-3 w-28 rounded-full bg-gray-100 dark:bg-gray-800" />
                <div className="h-3 w-24 rounded-full bg-gray-100 dark:bg-gray-800" />
                <div className="h-3 w-20 rounded-full bg-gray-100 dark:bg-gray-800" />
              </div>
            </div>
            <div className="hidden gap-2 sm:flex">
              <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function VipBadge({ compact = false }) {
  return (
    <span className={`inline-flex items-center rounded-lg border border-violet-200 bg-violet-50 font-medium text-violet-700 dark:border-violet-900/60 dark:bg-violet-950/20 dark:text-violet-300 ${
      compact ? 'px-2 py-1 text-[11px]' : 'px-2.5 py-1 text-xs'
    }`}>
      <Star className={`${compact ? 'mr-1 h-3 w-3' : 'mr-1 h-3.5 w-3.5'} fill-current`} />
      VIP
    </span>
  );
}

function UndesirableBadge({ compact = false }) {
  return (
    <span className={`inline-flex items-center rounded-lg border border-red-200 bg-red-50 font-medium text-red-700 dark:border-red-900/60 dark:bg-red-950/20 dark:text-red-300 ${compact ? 'px-2 py-1 text-[11px]' : 'px-2.5 py-1 text-xs'}`}>
      <XCircle className={`${compact ? 'mr-1 h-3 w-3' : 'mr-1 h-3.5 w-3.5'}`} />
      Нежелательный
    </span>
  );
}

function SectionModal({ open, onClose, title, size = 'max-w-3xl', children, footer }) {
  const closeWithHaptic = () => {
    haptic('close');
    onClose();
  };

  return (
    <ModalShell
      open={open}
      onClose={closeWithHaptic}
      variant="sheet"
      zIndexClass="z-[70]"
      containerClassName="p-0 sm:p-4"
      panelClassName={`flex max-h-[92vh] flex-col overflow-hidden sm:max-h-[90vh] ${size}`}
    >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 dark:border-gray-800 dark:bg-gray-900 sm:px-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
            <button
              type="button"
              onClick={closeWithHaptic}
            className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">{children}</div>
        {footer ? (
          <div className="sticky bottom-0 border-t border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900 sm:p-5">
            {footer}
          </div>
        ) : null}
    </ModalShell>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      {Icon ? <Icon className="mt-0.5 h-4 w-4 text-gray-400" /> : <div className="mt-1 h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-600" />}
      <div className="min-w-0 flex-1">
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
        <div className="break-words text-sm text-gray-900 dark:text-gray-100">{value}</div>
      </div>
    </div>
  );
}

function ClientDetailsModal({ client, open, onClose, onEdit, onShowOrders, onShowHistory }) {
  return (
    <SectionModal
      open={open}
      onClose={onClose}
      title="Карточка клиента"
      size="max-w-4xl"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              haptic('open');
              onShowHistory(client);
            }}
            className="flex-1 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-violet-700"
          >
            История
          </button>
          <button
            type="button"
            onClick={() => {
              haptic('open');
              onShowOrders(client);
            }}
            className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Заказы
          </button>
          <button
            type="button"
            onClick={() => {
              haptic('open');
              onEdit(client);
            }}
            className="flex-1 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            Редактировать
          </button>
        </div>
      }
    >
      {!client ? null : (
        <div className="space-y-5">
          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-800/60">
            <div className="flex items-start gap-3">
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white ${client.type === 'company' ? 'bg-blue-600' : 'bg-emerald-600'}`}>
                {client.type === 'company' ? <BriefcaseBusiness className="h-5 w-5" /> : <User className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{getClientName(client)}</div>
                {getClientSubtitle(client) ? <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{getClientSubtitle(client)}</div> : null}
                {client.vip ? <div className="mt-3"><VipBadge /></div> : null}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">Заказов</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{client.total_orders || 0}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">Потрачено</span>
                <span className="font-semibold text-gray-900 dark:text-gray-100">{formatMoney(client.total_spent)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 dark:text-gray-400">Бонусы</span>
                <span className="font-semibold text-violet-600 dark:text-violet-400">{Number(client.bonus_balance || 0).toLocaleString('ru-RU')}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div className="space-y-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                <div className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Основное</div>
                <div className="space-y-2 text-sm">
                  <InfoRow icon={Phone} label="Телефон" value={formatPhoneNumber(client.phone)} />
                  {client.referral_source ? <InfoRow label="Источник" value={getReferralSourceLabel(client.referral_source)} /> : null}
                  {client.type === 'individual' ? (
                    <>
                      <InfoRow label="Фамилия" value={client.last_name || '—'} />
                      <InfoRow label="Имя" value={client.first_name || '—'} />
                      <InfoRow label="Отчество" value={client.middle_name || '—'} />
                    </>
                  ) : (
                    <>
                      <InfoRow label="Контактное лицо" value={client.contact_person || '—'} />
                      <InfoRow label="ИНН" value={client.inn || '—'} />
                      <InfoRow label="КПП" value={client.kpp || '—'} />
                      <InfoRow label="Тип юрлица" value={client.legal_type || '—'} />
                    </>
                  )}
                </div>
              </div>

              {client.notes ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/60 dark:bg-amber-950/20">
                  <div className="mb-2 text-sm font-semibold text-amber-800 dark:text-amber-200">Заметка</div>
                  <div className="whitespace-pre-wrap break-words text-sm text-amber-900 dark:text-amber-100">{client.notes}</div>
                </div>
              ) : null}
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                <div className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Адреса</div>
                {client.addresses?.length ? (
                  <div className="space-y-2">
                    {client.addresses.map((address, index) => (
                      <div key={`${address.address_full}-${index}`} className="rounded-xl bg-gray-50 px-3 py-3 dark:bg-gray-800">
                        <div className="flex items-start gap-2">
                          <MapPin className="mt-0.5 h-4 w-4 text-gray-400" />
                          <div className="min-w-0 flex-1">
                            {address.address_label ? <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{address.address_label}</div> : null}
                            <div className="break-words text-sm text-gray-600 dark:text-gray-300">{address.address_full}</div>
                            {(address.entrance || address.floor || address.door_code) ? (
                              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {[address.entrance && `Подъезд: ${address.entrance}`, address.floor && `Этаж: ${address.floor}`, address.door_code && `Код: ${address.door_code}`].filter(Boolean).join(', ')}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">Адреса не сохранены</div>
                )}
              </div>

              {client.type === 'company' && (
                <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                  <div className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Реквизиты</div>
                  <div className="space-y-2 text-sm">
                    {client.short_name ? <InfoRow label="Краткое наименование" value={client.short_name} /> : null}
                    {client.full_name ? <InfoRow label="Полное наименование" value={client.full_name} /> : null}
                    {client.ogrn ? <InfoRow label="ОГРН" value={client.ogrn} /> : null}
                    {client.position ? <InfoRow label="Должность" value={client.position} /> : null}
                    {client.bank_name ? <InfoRow label="Банк" value={client.bank_name} /> : null}
                    {client.bank_account ? <InfoRow label="Расчетный счет" value={client.bank_account} /> : null}
                    {client.bik ? <InfoRow label="БИК" value={client.bik} /> : null}
                    {client.correspondent_account ? <InfoRow label="Корр. счет" value={client.correspondent_account} /> : null}
                    {client.legal_address ? <InfoRow icon={MapPin} label="Юридический адрес" value={client.legal_address} /> : null}
                    {!client.short_name && !client.full_name && !client.ogrn && !client.position && !client.bank_name && !client.bank_account && !client.bik && !client.correspondent_account && !client.legal_address ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400">Реквизиты не заполнены</div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </SectionModal>
  );
}

function ClientOrdersModal({ open, onClose, orders, clientName }) {
  return (
    <SectionModal open={open} onClose={onClose} title={`Заказы клиента${clientName ? ` · ${clientName}` : ''}`} size="max-w-4xl">
      {!orders.length ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">У клиента пока нет заказов</div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <div key={order.id} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-gray-900 dark:text-gray-100">Заказ {order.order_number || `#${order.id}`}</div>
                  <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {SERVICE_TYPE_LABELS[order.service_type] || order.service_type || '—'} · {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status || '—'}
                  </div>
                  <div className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    {order.address_text || 'Адрес не указан'}
                  </div>
                </div>
                <span className="rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/20 dark:text-blue-300">
                  {ORDER_STATUS_LABELS[order.status] || order.status || '—'}
                </span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Сумма</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{formatMoney(order.final_amount)}</div>
                </div>
                <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Создан</div>
                  <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{formatDateTime(order.created_date)}</div>
                </div>
                <div className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Завершен</div>
                  <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{formatDateTime(order.completed_date)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionModal>
  );
}

function ClientHistoryModal({ open, onClose, history, clientName }) {
  return (
    <SectionModal open={open} onClose={onClose} title={`История клиента${clientName ? ` · ${clientName}` : ''}`} size="max-w-4xl">
      {!history.length ? (
        <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">История изменений пока пуста</div>
      ) : (
        <div className="space-y-3">
          {history.map((item) => (
            <div key={item.id} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.summary || item.action_label || item.action}</div>
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {item.operator_name || 'Система'} · {formatDateTime(item.created_date)}
                  </div>
                </div>
              </div>
              {(item.old_value || item.new_value) ? (
                <details className="mt-3 rounded-xl bg-gray-50 p-3 dark:bg-gray-800">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-200">Показать детали</summary>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Было</div>
                      <pre className="whitespace-pre-wrap break-words rounded-lg bg-white p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-200">
                        {JSON.stringify(item.old_value ?? null, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Стало</div>
                      <pre className="whitespace-pre-wrap break-words rounded-lg bg-white p-3 text-xs text-gray-700 dark:bg-gray-900 dark:text-gray-200">
                        {JSON.stringify(item.new_value ?? null, null, 2)}
                      </pre>
                    </div>
                  </div>
                </details>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </SectionModal>
  );
}

function Field({ label, children, className = '' }) {
  const childArray = React.Children.toArray(children);
  const control = childArray[0];
  const helperContent = childArray.slice(1);

  return (
    <div className={className}>
      <FloatingField label={label}>{control}</FloatingField>
      {helperContent}
    </div>
  );
}

function ClientEditModal({ client, open, onClose, onSave, saving }) {
  const [formData, setFormData] = useState(null);
  const phoneInputRef = useRef(null);
  const isPhoneValid = !!normalizePhoneForSubmit(formData?.phone || '');

  useEffect(() => {
    if (!client) {
      setFormData(null);
      return;
    }
    setFormData({
      type: client.type,
      phone: maskPhoneInput(client.phone || ''),
      last_name: client.last_name || '',
      first_name: client.first_name || '',
      middle_name: client.middle_name || '',
      company_name: client.company_name || '',
      contact_person: client.contact_person || '',
      inn: client.inn || '',
      kpp: client.kpp || '',
      legal_type: client.legal_type || '',
      full_name: client.full_name || '',
      short_name: client.short_name || '',
      ogrn: client.ogrn || '',
      position: client.position || '',
      bank_name: client.bank_name || '',
      bank_account: client.bank_account || '',
      bik: client.bik || '',
      correspondent_account: client.correspondent_account || '',
      legal_address: client.legal_address || '',
      notes: client.notes || '',
      vip: !!client.vip,
      undesirable: !!client.undesirable
    });
  }, [client]);

  if (!formData) return null;

  const handlePhoneKeyDown = (event) => {
    if (event.key !== 'Backspace' && event.key !== 'Delete') {
      return;
    }

    const input = event.currentTarget;
    const value = String(input.value || '');
    const selectionStart = input.selectionStart ?? value.length;
    const selectionEnd = input.selectionEnd ?? value.length;

    if (selectionStart !== selectionEnd) {
      return;
    }

    const localDigits = getPhoneLocalDigits(value).split('');
    if (!localDigits.length) {
      return;
    }

    const digitsBeforeCursor = Array.from(value.slice(0, selectionStart)).reduce((count, char, index) => {
      if (!/\d/.test(char)) {
        return count;
      }
      if (value.startsWith('+7') && index === 1) {
        return count;
      }
      return count + 1;
    }, 0);

    const removeIndex = event.key === 'Backspace' ? digitsBeforeCursor - 1 : digitsBeforeCursor;
    if (removeIndex < 0 || removeIndex >= localDigits.length) {
      return;
    }

    event.preventDefault();
    localDigits.splice(removeIndex, 1);
    const nextMaskedValue = maskPhoneInput(localDigits.join(''));
    setFormData((prev) => ({ ...prev, phone: nextMaskedValue }));

    requestAnimationFrame(() => {
      const nextInput = phoneInputRef.current;
      if (nextInput) {
        const nextCaret = nextMaskedValue.length;
        nextInput.setSelectionRange(nextCaret, nextCaret);
      }
    });
  };

  return (
    <SectionModal
      open={open}
      onClose={onClose}
      title="Редактирование клиента"
      size="max-w-3xl"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              haptic('close');
              onClose();
            }}
            className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => onSave(formData)}
            disabled={saving || !isPhoneValid || (formData.type === 'company' && !formData.company_name)}
            className={`flex flex-1 items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-colors ${
              saving || !isPhoneValid || (formData.type === 'company' && !formData.company_name)
                ? 'bg-blue-300'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Сохранить
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
          <div className="flex items-center justify-center rounded-lg bg-white px-3 py-2 text-sm font-medium text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400">
            {formData.type === 'company' ? <BriefcaseBusiness className="mr-2 h-4 w-4" /> : <User className="mr-2 h-4 w-4" />}
            {formData.type === 'company' ? 'Юридическое лицо' : 'Физическое лицо'}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <Field label="Телефон *">
            <input
              ref={phoneInputRef}
              type="tel"
              value={formData.phone}
              onChange={(event) => setFormData((prev) => ({ ...prev, phone: maskPhoneInput(event.target.value) }))}
              onKeyDown={handlePhoneKeyDown}
              inputMode="tel"
              autoComplete="tel"
              placeholder="+7(999)-123-45-67"
              className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">Формат: +7(XXX)-XXX-XX-XX</div>
          </Field>
        </div>

        {formData.type === 'individual' ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field label="Фамилия">
              <input type="text" value={formData.last_name} onChange={(event) => setFormData((prev) => ({ ...prev, last_name: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Имя">
              <input type="text" value={formData.first_name} onChange={(event) => setFormData((prev) => ({ ...prev, first_name: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Отчество">
              <input type="text" value={formData.middle_name} onChange={(event) => setFormData((prev) => ({ ...prev, middle_name: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="Название компании *">
              <input type="text" value={formData.company_name} onChange={(event) => setFormData((prev) => ({ ...prev, company_name: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Контактное лицо">
              <input type="text" value={formData.contact_person} onChange={(event) => setFormData((prev) => ({ ...prev, contact_person: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="ИНН">
              <input type="text" value={formData.inn} onChange={(event) => setFormData((prev) => ({ ...prev, inn: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="КПП">
              <input type="text" value={formData.kpp} onChange={(event) => setFormData((prev) => ({ ...prev, kpp: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Тип юрлица">
              <input type="text" value={formData.legal_type} onChange={(event) => setFormData((prev) => ({ ...prev, legal_type: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="ОГРН">
              <input type="text" value={formData.ogrn} onChange={(event) => setFormData((prev) => ({ ...prev, ogrn: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Краткое наименование">
              <input type="text" value={formData.short_name} onChange={(event) => setFormData((prev) => ({ ...prev, short_name: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Должность">
              <input type="text" value={formData.position} onChange={(event) => setFormData((prev) => ({ ...prev, position: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field className="md:col-span-2" label="Полное наименование">
              <input type="text" value={formData.full_name} onChange={(event) => setFormData((prev) => ({ ...prev, full_name: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field className="md:col-span-2" label="Юридический адрес">
              <textarea value={formData.legal_address} onChange={(event) => setFormData((prev) => ({ ...prev, legal_address: event.target.value }))} rows={3} className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Банк">
              <input type="text" value={formData.bank_name} onChange={(event) => setFormData((prev) => ({ ...prev, bank_name: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Расчетный счет">
              <input type="text" value={formData.bank_account} onChange={(event) => setFormData((prev) => ({ ...prev, bank_account: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="БИК">
              <input type="text" value={formData.bik} onChange={(event) => setFormData((prev) => ({ ...prev, bik: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
            <Field label="Корр. счет">
              <input type="text" value={formData.correspondent_account} onChange={(event) => setFormData((prev) => ({ ...prev, correspondent_account: event.target.value }))} className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
            </Field>
          </div>
        )}

        <Field label="Заметка">
          <textarea
            value={formData.notes}
            onChange={(event) => setFormData((prev) => ({ ...prev, notes: event.target.value.slice(0, CLIENT_NOTE_MAX_LENGTH) }))}
            maxLength={CLIENT_NOTE_MAX_LENGTH}
            rows={3}
            className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          />
          <div className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500">
            {formData.notes.length}/{CLIENT_NOTE_MAX_LENGTH}
          </div>
        </Field>

        <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 text-sm text-gray-700 dark:border-gray-800 dark:bg-gray-800/60 dark:text-gray-200">
          <input type="checkbox" checked={formData.vip} onChange={(event) => setFormData((prev) => ({ ...prev, vip: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span className="flex-1">
            <span className="block font-medium">VIP клиент</span>
            <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">Показывается отдельной меткой в клиентах и в списках заказов</span>
          </span>
        </label>
        <label className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-200">
          <input type="checkbox" checked={formData.undesirable} onChange={(event) => setFormData((prev) => ({ ...prev, undesirable: event.target.checked }))} className="h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500" />
          <span className="flex-1">
            <span className="block font-medium">Нежелательный клиент</span>
            <span className="mt-0.5 block text-xs text-red-600/80 dark:text-red-300/80">При создании заказа потребуется дополнительное подтверждение</span>
          </span>
        </label>
      </div>
    </SectionModal>
  );
}

function ClientExportModal({ open, onClose, defaultType, filters, pagination, onExport, exporting }) {
  const [clientKind, setClientKind] = useState(defaultType);
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [applyCurrentFilters, setApplyCurrentFilters] = useState(true);
  const [exportScope, setExportScope] = useState('all_filtered');
  const [selectedFields, setSelectedFields] = useState(EXPORT_DEFAULT_FIELDS[defaultType] || []);
  const [error, setError] = useState('');

  const fieldLabels = useMemo(() => CLIENT_EXPORT_FIELD_LABELS[clientKind] || {}, [clientKind]);
  const fieldGroups = useMemo(() => EXPORT_FIELD_GROUPS[clientKind] || [], [clientKind]);
  const orderedFields = useMemo(() => getExportFieldOrder(clientKind), [clientKind]);

  const activeFilterSummary = useMemo(() => {
    const parts = [];
    if (filters?.search?.trim()) parts.push(`Поиск: ${filters.search.trim()}`);
    if (filters?.vip) parts.push('Только VIP');
    return parts;
  }, [filters]);

  useEffect(() => {
    if (open) {
      setClientKind(defaultType);
      setExportFormat('xlsx');
      setApplyCurrentFilters(true);
      setExportScope('all_filtered');
      setSelectedFields([...(EXPORT_DEFAULT_FIELDS[defaultType] || [])]);
      setError('');
    }
  }, [defaultType, open]);

  const reorderFields = (nextFields) => {
    const fieldSet = new Set(nextFields);
    return orderedFields.filter((fieldName) => fieldSet.has(fieldName));
  };

  const handleKindChange = (value) => {
    setClientKind(value);
    setSelectedFields([...(EXPORT_DEFAULT_FIELDS[value] || [])]);
    setError('');
  };

  const toggleField = (fieldName) => {
    setSelectedFields((prev) => (
      prev.includes(fieldName)
        ? prev.filter((item) => item !== fieldName)
        : reorderFields([...prev, fieldName])
    ));
  };

  const setGroupSelection = (fields, enabled) => {
    setSelectedFields((prev) => {
      if (enabled) {
        return reorderFields([...prev, ...fields]);
      }
      return prev.filter((fieldName) => !fields.includes(fieldName));
    });
  };

  const handleExport = async () => {
    if (!selectedFields.length) {
      setError('Выбери хотя бы одно поле для выгрузки');
      return;
    }
    setError('');
    try {
      await onExport({
        clientKind,
        format: exportFormat,
        applyCurrentFilters: exportScope === 'current_page' ? true : applyCurrentFilters,
        scope: exportScope,
        fields: selectedFields,
        skip: pagination?.skip || 0,
        limit: pagination?.limit || 50
      });
      onClose?.();
    } catch (exportError) {
      setError(exportError.message || 'Не удалось выгрузить клиентскую базу');
    }
  };

  return (
    <SectionModal
      open={open}
      onClose={onClose}
      title="Экспорт клиентской базы"
      size="max-w-5xl"
      footer={
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => {
              haptic('close');
              onClose();
            }}
            className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Закрыть
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || !selectedFields.length}
            className={`flex flex-1 items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-colors ${
              exporting || !selectedFields.length
                ? 'bg-blue-300'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
            Скачать
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div>
              <div className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Тип клиентов</div>
              <ManagementSegmentedControl value={clientKind} onChange={handleKindChange} compact options={CLIENT_TYPE_OPTIONS} />
            </div>

            <div>
              <div className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Формат</div>
              <ManagementSegmentedControl value={exportFormat} onChange={setExportFormat} compact options={EXPORT_FORMAT_OPTIONS} />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setExportScope('all_filtered')}
                className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                  exportScope === 'all_filtered'
                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/20 dark:text-blue-300'
                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <div className="text-sm font-medium">Все подходящие</div>
                <div className="mt-1 text-xs opacity-80">Выгрузить все записи выбранного типа</div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setExportScope('current_page');
                  setApplyCurrentFilters(true);
                }}
                className={`rounded-xl border px-3 py-3 text-left transition-colors ${
                  exportScope === 'current_page'
                    ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900/60 dark:bg-blue-950/20 dark:text-blue-300'
                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <div className="text-sm font-medium">Текущая страница</div>
                <div className="mt-1 text-xs opacity-80">До {pagination?.limit || 50} записей из текущего списка</div>
              </button>
            </div>

            <label className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2.5 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              <input type="checkbox" checked={exportScope === 'current_page' ? true : applyCurrentFilters} disabled={exportScope === 'current_page'} onChange={(event) => setApplyCurrentFilters(event.target.checked)} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60" />
              {exportScope === 'current_page' ? 'Для текущей страницы фильтры учитываются автоматически' : 'Учитывать текущий поиск и фильтры'}
            </label>
          </div>

          <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Что попадет в файл</div>
              <div className="mt-2 space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>{clientKind === 'company' ? 'Юридические лица' : 'Физические лица'} в формате {exportFormat === 'xlsx' ? 'Excel' : 'Word'}.</p>
                <p>{exportScope === 'current_page' ? `Текущая страница, до ${pagination?.limit || 50} записей.` : 'Все подходящие записи выбранного типа.'}</p>
                <p>{applyCurrentFilters ? (activeFilterSummary.length ? activeFilterSummary.join(' · ') : 'Активных фильтров нет, выгрузятся все записи выбранного типа.') : 'Текущие фильтры будут проигнорированы.'}</p>
              </div>
            </div>

            <div className="rounded-xl bg-gray-50 px-3 py-3 dark:bg-gray-800">
              <div className="text-xs text-gray-500 dark:text-gray-400">Полей выбрано</div>
              <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{selectedFields.length}</div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setSelectedFields([...(EXPORT_DEFAULT_FIELDS[clientKind] || [])])} className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">Набор по умолчанию</button>
              <button type="button" onClick={() => setSelectedFields([...orderedFields])} className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">Выбрать все</button>
              <button type="button" onClick={() => setSelectedFields([])} className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">Очистить</button>
            </div>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/20 dark:text-red-300">
            {error}
          </div>
        ) : null}

        <div className="space-y-3">
          {fieldGroups.map((group) => {
            const selectedCount = group.fields.filter((fieldName) => selectedFields.includes(fieldName)).length;
            const allSelected = selectedCount === group.fields.length;
            return (
              <div key={group.title} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{group.title}</div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{selectedCount} из {group.fields.length}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGroupSelection(group.fields, !allSelected)}
                    className="rounded-lg bg-gray-100 px-3 py-2 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    {allSelected ? 'Снять группу' : 'Выбрать группу'}
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {group.fields.map((fieldName) => {
                    const checked = selectedFields.includes(fieldName);
                    return (
                      <label
                        key={fieldName}
                        className={`flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2.5 transition-colors ${
                          checked
                            ? 'border-blue-200 bg-blue-50 dark:border-blue-900/60 dark:bg-blue-950/20'
                            : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800/70'
                        }`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleField(fieldName)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className={`text-sm ${checked ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-200'}`}>
                          {fieldLabels[fieldName] || fieldName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SectionModal>
  );
}

function ActionIconButton({ title, onClick, icon: Icon, tone = 'gray' }) {
  const toneClasses = {
    blue: 'text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/20',
    emerald: 'text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/20',
    violet: 'text-violet-600 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-950/20',
    gray: 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
  };
  return (
    <button type="button" title={title} onClick={onClick} className={`rounded-lg p-2 transition-colors ${toneClasses[tone] || toneClasses.gray}`}>
      <Icon className="h-4.5 w-4.5" />
    </button>
  );
}

export default function ClientsManagement() {
  const [activeTab, setActiveTab] = useState('individual');
  const [loading, setLoading] = useState(true);
  const [savingClient, setSavingClient] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ search: '', vip: false, undesirable: false });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0 });
  const [sort, setSort] = useState({ field: 'last_order_date', direction: 'desc' });
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef(null);
  const clientsRequestIdRef = useRef(0);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedHistory, setSelectedHistory] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [phoneChangeConfirm, setPhoneChangeConfirm] = useState(null);
  const { toast, showToast, hideToast } = useToast();

  useModalBackButton(showDetailsModal, () => setShowDetailsModal(false));
  useModalBackButton(showEditModal, () => setShowEditModal(false));
  useModalBackButton(showOrdersModal, () => setShowOrdersModal(false));
  useModalBackButton(showHistoryModal, () => setShowHistoryModal(false));
  useModalBackButton(showExportModal, () => setShowExportModal(false));
  useModalBackButton(!!phoneChangeConfirm, () => setPhoneChangeConfirm(null));

  const loadStats = useCallback(async () => {
    const response = await safeFetch(`${API_URL}/api/clients/directory/stats`);
    setStats(response);
  }, []);

  const loadClients = useCallback(async () => {
    const requestId = clientsRequestIdRef.current + 1;
    clientsRequestIdRef.current = requestId;
    const append = pagination.skip > 0;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('client_type', activeTab);
      if (filters.search.trim()) params.set('search', filters.search.trim());
      if (filters.vip) params.set('vip', 'true');
      if (filters.undesirable) params.set('undesirable', 'true');
      params.set('sort_by', sort.field);
      params.set('sort_dir', sort.direction);
      params.set('skip', String(pagination.skip));
      params.set('limit', String(pagination.limit));
      const response = await safeFetch(`${API_URL}/api/clients/directory/list?${params.toString()}`);
      if (requestId !== clientsRequestIdRef.current) return;
      const nextClients = response.clients || [];
      const total = Number(response.total || 0);
      setClients((prev) => (append ? [...prev, ...nextClients] : nextClients));
      setPagination((prev) => ({ ...prev, total }));
      setHasMore(pagination.skip + nextClients.length < total);
    } catch (error) {
      if (requestId !== clientsRequestIdRef.current) return;
      showToast(error.message || 'Не удалось загрузить клиентов', 'error');
      if (!append) setClients([]);
    } finally {
      if (requestId === clientsRequestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [activeTab, filters.search, filters.vip, filters.undesirable, pagination.limit, pagination.skip, showToast, sort.direction, sort.field]);

  useEffect(() => {
    loadStats().catch(() => undefined);
  }, [loadStats]);

  useEffect(() => {
    loadClients().catch(() => undefined);
  }, [loadClients]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      setPagination((prev) => {
        if (prev.skip + prev.limit >= prev.total) return prev;
        return { ...prev, skip: prev.skip + prev.limit };
      });
    }, { rootMargin: '320px 0px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore]);

  const fetchClientProfile = useCallback(async (client) => {
    const type = client?.type || activeTab;
    const clientId = client?.id;
    if (!clientId) return null;
    const [details, addresses] = await Promise.all([
      safeFetch(`${API_URL}/api/clients/${type}/${clientId}`),
      safeFetch(`${API_URL}/api/clients/${type}/${clientId}/addresses`)
    ]);
    return { ...details, type, addresses: addresses || [] };
  }, [activeTab]);

  const handleOpenDetails = useCallback(async (client) => {
    try {
      haptic('open');
      const profile = await fetchClientProfile(client);
      setSelectedClient(profile);
      setShowDetailsModal(true);
    } catch (error) {
      showToast(error.message || 'Не удалось открыть карточку клиента', 'error');
    }
  }, [fetchClientProfile, showToast]);

  const handleOpenEdit = useCallback(async (client) => {
    try {
      haptic('open');
      const profile = await fetchClientProfile(client);
      setSelectedClient(profile);
      setShowEditModal(true);
    } catch (error) {
      showToast(error.message || 'Не удалось открыть редактирование', 'error');
    }
  }, [fetchClientProfile, showToast]);

  const handleOpenOrders = useCallback(async (client) => {
    try {
      haptic('open');
      const type = client?.type || activeTab;
      const response = await safeFetch(`${API_URL}/api/clients/${type}/${client.id}/orders`);
      setSelectedOrders(response.orders || []);
      setSelectedClient((prev) => (prev?.id === client.id ? prev : client));
      setShowOrdersModal(true);
    } catch (error) {
      showToast(error.message || 'Не удалось загрузить заказы клиента', 'error');
    }
  }, [activeTab, showToast]);

  const handleOpenHistory = useCallback(async (client) => {
    try {
      haptic('open');
      const type = client?.type || activeTab;
      const response = await safeFetch(`${API_URL}/api/clients/${type}/${client.id}/history`);
      setSelectedHistory(response.history || []);
      setSelectedClient((prev) => (prev?.id === client.id ? prev : client));
      setShowHistoryModal(true);
    } catch (error) {
      showToast(error.message || 'Не удалось загрузить историю клиента', 'error');
    }
  }, [activeTab, showToast]);

  const findPhoneDuplicates = useCallback(async (phone, clientType, clientId) => {
    const normalizedPhone = normalizePhoneForSubmit(phone);
    if (!normalizedPhone) {
      return [];
    }

    const response = await safeFetch(`${API_URL}/api/clients/search/${encodeURIComponent(normalizedPhone)}`);
    const duplicates = [
      ...((response.individuals || []).map((client) => ({ ...client, type: 'individual' }))),
      ...((response.companies || []).map((client) => ({ ...client, type: 'company' }))),
    ];

    return duplicates.filter((client) => !(client.type === clientType && Number(client.id) === Number(clientId)));
  }, []);

  const handleSaveClient = useCallback(async (formData, options = {}) => {
    if (!selectedClient?.id) return;
    setSavingClient(true);
    hideToast();
    try {
      const normalizedPhone = normalizePhoneForSubmit(formData.phone);
      if (!normalizedPhone) {
        throw new Error('Укажи телефон в формате +7(XXX)-XXX-XX-XX');
      }

      const currentPhone = normalizePhoneForSubmit(selectedClient.phone || '');
      const phoneChanged = normalizedPhone !== currentPhone;
      if (phoneChanged) {
        const duplicates = await findPhoneDuplicates(normalizedPhone, formData.type, selectedClient.id);
        if (duplicates.length) {
          const duplicate = duplicates[0];
          const duplicateName = duplicate.type === 'company'
            ? duplicate.company_name
            : [duplicate.last_name, duplicate.first_name, duplicate.middle_name].filter(Boolean).join(' ').trim();
          throw new Error(`Клиент с таким телефоном уже существует: ${duplicateName || 'без имени'}`);
        }

        if (!options.skipPhoneWarning && Number(selectedClient.total_orders || 0) > 0) {
          setPhoneChangeConfirm({
            formData: { ...formData, phone: maskPhoneInput(normalizedPhone) },
            previousPhone: formatPhoneNumber(selectedClient.phone),
            nextPhone: maskPhoneInput(normalizedPhone),
            totalOrders: Number(selectedClient.total_orders || 0),
          });
          haptic('warning');
          return;
        }
      }

      const payload = { ...formData, phone: normalizedPhone };
      delete payload.type;
      delete payload.preferred_pickup_time;
      delete payload.preferred_delivery_time;
      delete payload.preferred_payment_method;
      delete payload.referral_source;
      delete payload.referral_code;
      delete payload.active;
      delete payload.bonus_balance;

      setPhoneChangeConfirm(null);
      await safeFetch(`${API_URL}/api/clients/${formData.type}/${selectedClient.id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      haptic('success');
      showToast('Данные клиента сохранены', 'success');
      setShowEditModal(false);
      const refreshedProfile = await fetchClientProfile({ id: selectedClient.id, type: formData.type });
      setSelectedClient(refreshedProfile);
      await Promise.all([loadClients(), loadStats()]);
    } catch (error) {
      haptic('error');
      showToast(error.message || 'Не удалось сохранить клиента', 'error');
    } finally {
      setSavingClient(false);
    }
  }, [fetchClientProfile, findPhoneDuplicates, hideToast, loadClients, loadStats, selectedClient, showToast]);

  const handleExportClients = useCallback(async ({ clientKind, format, applyCurrentFilters, scope, fields, skip, limit }) => {
    setExportLoading(true);
    hideToast();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/clients/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          client_kind: clientKind,
          export_format: format,
          fields,
          apply_filters: applyCurrentFilters,
          filters: applyCurrentFilters ? {
            search: filters.search.trim() || null,
            vip: filters.vip ? true : null
            , undesirable: filters.undesirable ? true : null
          } : null,
          scope,
          skip,
          limit
        })
      });

      if (!response.ok) {
        let errorMessage = `Ошибка ${response.status}`;
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json();
          errorMessage = data?.detail || data?.message || errorMessage;
        } else {
          const text = await response.text();
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const fileName = response.headers.get('X-Export-Filename') || buildClientExportFileName({ clientKind, format });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      haptic('success');
      showToast('Клиентская база выгружена', 'success');
    } catch (error) {
      haptic('error');
      throw error;
    } finally {
      setExportLoading(false);
    }
  }, [filters, hideToast, showToast]);

  const activeSortOption = CLIENT_SORT_OPTIONS.find((option) => (
    option.field === sort.field && option.direction === sort.direction
  )) || CLIENT_SORT_OPTIONS[0];
  const ActiveSortIcon = activeSortOption.icon;

  return (
    <div className="space-y-3 animate-fade-in desktop-content-container-wide">
            {stats ? (
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900 animate-slide-up">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <span className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Users className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              Всего
              <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.total_clients}</span>
            </span>
            <span className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <Star className="h-4 w-4 fill-current text-violet-500 dark:text-violet-400" />
              VIP
              <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.vip_clients}</span>
            </span>
            <span className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />
              Нежелательные
              <span className="font-semibold text-gray-900 dark:text-gray-100">{stats.undesirable_clients}</span>
            </span>
          </div>
        </div>
      ) : null}

      <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-gray-900 animate-slide-up">
        <div className="mb-2 flex items-center justify-center gap-2">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Тип клиентов</span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
        <ManagementSegmentedControl value={activeTab} onChange={(value) => { setActiveTab(value); setPagination((prev) => ({ ...prev, skip: 0 })); }} compact options={CLIENT_TYPE_OPTIONS} />
      </div>

      <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-gray-900 animate-slide-up">
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) => { setFilters((prev) => ({ ...prev, search: event.target.value })); setPagination((prev) => ({ ...prev, skip: 0 })); }}
              placeholder={activeTab === 'company' ? 'Поиск по компании, телефону, ИНН' : 'Поиск по ФИО или телефону'}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-10 pr-4 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>

          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => { haptic('selection'); setFilters((prev) => ({ ...prev, vip: !prev.vip })); setPagination((prev) => ({ ...prev, skip: 0 })); }} className={`inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-medium transition-colors ${filters.vip ? 'bg-white text-blue-600 shadow-sm ring-1 ring-blue-200 dark:bg-gray-900 dark:text-blue-400 dark:ring-blue-900/50' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                <Star className={`mr-2 h-4 w-4 ${filters.vip ? 'fill-current' : ''}`} />
                VIP
              </button>

              <button type="button" onClick={() => { haptic('selection'); setFilters((prev) => ({ ...prev, undesirable: !prev.undesirable })); setPagination((prev) => ({ ...prev, skip: 0 })); }} className={`inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-medium transition-colors ${filters.undesirable ? 'bg-white text-red-600 shadow-sm ring-1 ring-red-200 dark:bg-gray-900 dark:text-red-400 dark:ring-red-900/50' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}>
                <XCircle className="mr-2 h-4 w-4" />
                Нежелательные
              </button>

              <button
                type="button"
                onClick={() => {
                  haptic('selection');
                  const currentIndex = CLIENT_SORT_OPTIONS.findIndex((option) => (
                    option.field === sort.field && option.direction === sort.direction
                  ));
                  const nextOption = CLIENT_SORT_OPTIONS[
                    (currentIndex + 1) % CLIENT_SORT_OPTIONS.length
                  ];
                  setSort({ field: nextOption.field, direction: nextOption.direction });
                  setPagination((prev) => ({ ...prev, skip: 0 }));
                }}
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-blue-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-blue-400"
                title={activeSortOption.label}
                aria-label={`Сортировка: ${activeSortOption.label}. Нажмите, чтобы изменить`}
              >
                <ActiveSortIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 text-xs text-gray-500 dark:text-gray-400 lg:text-right">Показано {clients.length} из {pagination.total}</div>

            <button type="button" onClick={() => { haptic('open'); setShowExportModal(true); }} className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700">
              <Download className="mr-2 h-4 w-4" />
              Экспорт
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow-sm dark:bg-gray-900 animate-slide-up">
        <div className="p-3 sm:p-4">
          {loading ? (
            <ClientsListSkeleton />
          ) : !clients.length ? (
            <div className="py-14 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500">
                <Users className="h-6 w-6" />
              </div>
              <div className="mt-4 text-sm font-medium text-gray-700 dark:text-gray-300">{activeTab === 'company' ? 'Юридические лица не найдены' : 'Физические лица не найдены'}</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Попробуй сменить фильтры или проверить выбранный тип клиентов</div>
            </div>
          ) : (
            <div className="space-y-2">
              {clients.map((client) => (
                <div key={`${client.type}-${client.id}`} className="rounded-2xl border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800/70">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-white ${client.type === 'company' ? 'bg-blue-600' : 'bg-emerald-600'}`}>
                        {client.type === 'company' ? <BriefcaseBusiness className="h-5 w-5" /> : <User className="h-5 w-5" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{getClientName(client)}</div>
                          {client.vip ? <VipBadge compact /> : null}
                          {client.undesirable ? <UndesirableBadge compact /> : null}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{formatPhoneNumber(client.phone)}</span>
                          {client.type === 'company' && client.inn ? <span className="inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5" />ИНН {client.inn}</span> : null}
                          {client.type === 'company' && client.contact_person ? <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{client.contact_person}</span> : null}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                          <span>Заказы: <span className="font-medium text-gray-900 dark:text-gray-100">{client.total_orders || 0}</span></span>
                          <span>Сумма: <span className="font-medium text-gray-900 dark:text-gray-100">{formatMoney(client.total_spent)}</span></span>
                          <span>Бонусы: <span className="font-medium text-violet-600 dark:text-violet-400">{Number(client.bonus_balance || 0).toLocaleString('ru-RU')}</span></span>
                          <span>Последний: <span className="font-medium text-gray-900 dark:text-gray-100">{client.last_order_date ? new Date(client.last_order_date).toLocaleDateString('ru-RU') : '—'}</span></span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 self-end xl:self-auto">
                      <ActionIconButton title="Карточка" onClick={() => handleOpenDetails(client)} icon={Eye} tone="blue" />
                      <ActionIconButton title="Редактировать" onClick={() => handleOpenEdit(client)} icon={Edit2} tone="emerald" />
                      <ActionIconButton title="Заказы" onClick={() => handleOpenOrders(client)} icon={ShoppingBag} tone="violet" />
                      <ActionIconButton title="История" onClick={() => handleOpenHistory(client)} icon={History} tone="gray" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div ref={loadMoreRef} className="flex min-h-12 items-center justify-center border-t border-gray-200 px-4 py-3 dark:border-gray-800">
          {loadingMore ? (
            <CompactListSkeleton count={2} className="w-full text-left" />
          ) : hasMore ? (
            <span className="text-xs text-gray-400 dark:text-gray-500">Прокрутите вниз, чтобы загрузить ещё</span>
          ) : clients.length ? (
            <span className="text-xs text-gray-400 dark:text-gray-500">Все клиенты загружены</span>
          ) : null}
        </div>
      </div>

      <ClientDetailsModal client={selectedClient} open={showDetailsModal} onClose={() => setShowDetailsModal(false)} onEdit={(client) => { setShowDetailsModal(false); handleOpenEdit(client); }} onShowOrders={(client) => { setShowDetailsModal(false); handleOpenOrders(client); }} onShowHistory={(client) => { setShowDetailsModal(false); handleOpenHistory(client); }} />
      <ClientEditModal client={selectedClient} open={showEditModal} onClose={() => setShowEditModal(false)} onSave={handleSaveClient} saving={savingClient} />
      <SectionModal
        open={!!phoneChangeConfirm}
        onClose={() => setPhoneChangeConfirm(null)}
        title="Изменение номера"
        size="max-w-md"
        footer={(
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                haptic('close');
                setPhoneChangeConfirm(null);
              }}
              className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={() => {
                if (!phoneChangeConfirm?.formData) return;
                handleSaveClient(phoneChangeConfirm.formData, { skipPhoneWarning: true });
              }}
              className="flex-1 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-amber-700"
            >
              Все равно сохранить
            </button>
          </div>
        )}
      >
        {phoneChangeConfirm ? (
          <div className="space-y-3 text-sm text-gray-700 dark:text-gray-200">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
              У клиента уже есть заказов: <span className="font-semibold">{phoneChangeConfirm.totalOrders}</span>
            </div>
            <div>
              Было: <span className="font-medium text-gray-900 dark:text-gray-100">{phoneChangeConfirm.previousPhone}</span>
            </div>
            <div>
              Станет: <span className="font-medium text-gray-900 dark:text-gray-100">{phoneChangeConfirm.nextPhone}</span>
            </div>
            <div className="text-gray-600 dark:text-gray-300">
              В заказах номер клиента отдельно не хранится. После сохранения в связанных заказах тоже начнет показываться новый номер.
            </div>
          </div>
        ) : null}
      </SectionModal>
      <ClientOrdersModal open={showOrdersModal} onClose={() => setShowOrdersModal(false)} orders={selectedOrders} clientName={getClientName(selectedClient)} />
      <ClientHistoryModal open={showHistoryModal} onClose={() => setShowHistoryModal(false)} history={selectedHistory} clientName={getClientName(selectedClient)} />
      <ClientExportModal open={showExportModal} onClose={() => setShowExportModal(false)} defaultType={activeTab} filters={filters} pagination={pagination} onExport={handleExportClients} exporting={exportLoading} />
    </div>
  );
}
